import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import Toast from '../../../components/support/Toast';
import BackupsTable from './components/BackupsTable';
import RemoteBackupStatus from './components/RemoteBackupStatus';
import { formatBackupSize, formatBackupDate } from './components/BackupRow';
import useAdminBackups from './hooks/useAdminBackups';
import useAdminBackupsRemote from './hooks/useAdminBackupsRemote';

/**
 * AdminBackups - Backups do banco de dados gerados pela rotina automática (Admin Geral)
 *
 * Responsabilidades:
 * - Consome useAdminBackups() para carregar a lista completa de backups disponíveis
 * - Renderiza AreaTitle + estatísticas (quantidade, mais recente, tamanho total) +
 *   botão "Atualizar" + tabela
 * - Orquestra o Toast de erro quando um download falha (delegado pelo BackupRow)
 */
function AdminBackups() {
  const {
    backups,
    total,
    totalSize,
    latest,
    directoryExists,
    isLoading,
    error,
    refresh,
  } = useAdminBackups();

  const {
    status: remoteStatus,
    isLoading: isRemoteLoading,
    error: remoteError,
    refresh: refreshRemote,
  } = useAdminBackupsRemote();

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const showDownloadError = (message) => {
    setToast({ open: true, message, severity: 'error' });
  };

  const handleRefresh = () => {
    refresh();
    refreshRemote();
  };

  const showEmptyWarning = !isLoading && !error && (!directoryExists || backups.length === 0);

  return (
    <Box sx={{ backgroundColor: '#F8F9FC', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <AreaTitle
          Title="Admin Geral · Backups"
          Icon={<BackupOutlinedIcon />}
          Description="Backups do banco de dados gerados pela rotina automática (retenção de 7 dias) e status do backup fora do servidor."
        />
      </Box>

      <RemoteBackupStatus status={remoteStatus} isLoading={isRemoteLoading} error={remoteError} />

      {showEmptyWarning && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Nenhum backup encontrado. A rotina roda pelo cron <code>backup_database.php</code> e mantém 7 dias.
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Backups: <strong>{total}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mais recente: <strong>{latest ? formatBackupDate(latest.created_at) : '—'}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tamanho total: <strong>{formatBackupSize(totalSize)}</strong>
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshOutlinedIcon />}
          onClick={handleRefresh}
          disabled={isLoading || isRemoteLoading}
        >
          Atualizar
        </Button>
      </Box>

      <BackupsTable
        backups={backups}
        isLoading={isLoading}
        error={error}
        onDownloadError={showDownloadError}
      />

      <Toast
        open={toast.open}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        message={toast.message}
        severity={toast.severity}
        autoHideDuration={5000}
      />
    </Box>
  );
}

export default AdminBackups;
